import type { TextUIPart } from '@ai-sdk/ui-utils';
import { IconButton } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { P, match } from 'ts-pattern';
import TablerIconCopy from '~icons/tabler/copy';
import { useNightingale } from '#/lib/nightingale';
import { useNotify } from '#/lib/notify';
import { cn } from '#/utils/styles';
import ChatCodeBlock from '../chat-codeblock';
import ChatGeneratedDiagram from '../chat-diagram';
import ChatMessagePartTextContainer from './chat-message-part-text-container';

export interface ChatMessagePartTextProps {
  messageId: string;
  part: TextUIPart;
}

function ChatMessagePartText({ messageId, part }: ChatMessagePartTextProps) {
  const notify = useNotify();
  const nightingale = useNightingale();

  return (
    <ChatMessagePartTextContainer>
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          pre: 'div',
          code(props) {
            const { children, className, ...rest } = props;
            const language = getLanguage(className);
            const childrenAsString = getChildrenAsString(children);

            function copyCode() {
              nightingale.capture(['copy', 'ai_chat'], {
                messageId,
                type: [part.type, 'code'].join('_'),
                content: childrenAsString,
              });

              const lang = match(language)
                .with(P.nullish, () => 'plaintext')
                .otherwise(() => language);

              navigator.clipboard.writeText(
                `\`\`\`${lang}\n${childrenAsString}\`\`\``
              );
              notify.send({
                message: 'Copied to clipboard',
                severity: 'success',
              });
            }

            return match({
              children,
              className,
              language,
              childrenAsString,
              ...rest,
            })
              .with({ language: 'mermaid' }, (px) => (
                <ChatGeneratedDiagram>{children}</ChatGeneratedDiagram>
              ))
              .with({ language: P.string }, ({ language, node, ...px }) => (
                <div className='relative rounded-md'>
                  <div className='absolute top-2 right-2 z-10'>
                    <IconButton size='small' onClick={copyCode}>
                      <TablerIconCopy />
                    </IconButton>
                  </div>
                  <ChatCodeBlock
                    {...px}
                    className={cn(className, 'relative z-0 rounded-md')}
                    code={childrenAsString}
                    language={language}
                  />
                </div>
              ))
              .otherwise(() => (
                <code
                  {...rest}
                  className={cn(
                    className,
                    'cursor-pointer font-mono bg-gray-900 text-gray-400 rounded-md px-1 py-0.5 hover:bg-gray-800 text-[90%]'
                  )}
                  onClick={copyCode}
                >
                  {childrenAsString.replace(/\n$/, '')}
                </code>
              ));
          },
        }}
      >
        {match(part)
          .with({ type: 'text' }, ({ text }) => text)
          .otherwise(() => '')}
      </Markdown>
    </ChatMessagePartTextContainer>
  );
}

export default React.memo(ChatMessagePartText);

/*
|------------------
| Internals
|------------------
*/

function getLanguage(className?: string) {
  const language = /language-(\w+)/.exec(className || '')?.[1];

  if (!language) {
    return null;
  }

  return language;
}

function getChildrenAsString(children: React.ReactNode) {
  return _.toString(children);
}
